<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Creating_the_Metamodel/Language_Structure';
    let nextLink= '/Documentation/Defining_an_Editor';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="defining-properties-1"
  bind:intersecting={visibleById["defining-properties-1"]}
  onIntersectChange={(visible) => setVisible("defining-properties-1", visible)}
>Defining Properties</SectionComponent>
<p>Each of the language structure elements (concepts, interfaces, expressions, etc.) may have <strong>Properties</strong>.<br>
Properties can be lists; this is indicated by square brackets after the type name. There are three types
of properties.</p>
<SectionComponent tag="h2" 
  id="primitive-properties-2"
  bind:intersecting={visibleById["primitive-properties-2"]}
  onIntersectChange={(visible) => setVisible("primitive-properties-2", visible)}
>Primitive Properties</SectionComponent>
<p><strong>Primitive properties</strong> have as type <code>identifier</code>, <code>string</code>, <code>number</code>, or <code>boolean</code>, and are
always contained in the <em>concept</em>. Primitive properties may also be lists.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L25-L26

name: identifier;               // internal name
isUnderConstruction: boolean;   // defines whether this base product is still 'raw'</code></pre>
<SectionComponent tag="h2" 
  id="part-properties-3"
  bind:intersecting={visibleById["part-properties-3"]}
  onIntersectChange={(visible) => setVisible("part-properties-3", visible)}
>Part Properties</SectionComponent>
<p><strong>Parts</strong> have as type one of the <em>concepts</em> (including <em>expression concepts</em> and <em>limited concepts</em>)
or interfaces in the language, and are <em>contained in the concept</em> that holds the property (as in
the UML composition relationship). Parts are also referred to as children.
In the example below <code>body</code> and <code>parameters</code> are parts. </p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L72-L73

body: DocuExpression;               // the actual calculation definition
parameters: Parameter[];            // any parameters</code></pre>
<SectionComponent tag="h2" 
  id="reference-properties-4"
  bind:intersecting={visibleById["reference-properties-4"]}
  onIntersectChange={(visible) => setVisible("reference-properties-4", visible)}
>Reference Properties</SectionComponent>
<p><strong>References</strong> also have as type one of the <em>concepts</em> or <em>interfaces</em> in the language, but a reference is not contained
in its <em>concept</em> (as in the UML association relationship). References are indicated by the keyword
<em>reference</em> in front of the definition.</p>
<p>References are always by name, therefore the referred concept must have a <code>name</code> property of type <code>identifier</code>.</p>
<p>In the following example the concept <code>InsuranceProduct</code> holds a list of references to <code>InsuranceParts</code> in the property <code>parts</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L53-L65

concept InsuranceProduct &#123;
    name: identifier;                       // internal name
    productName: string;                    // name by which this product is known to the public
    themes: InsuranceTheme[];               // the 'kind' of insurance
    advertisedPremium: EuroLiteral;         // the premium as known to the public
    nrPremiumDays: PremiumDays;             // the number of days for which the advertised premium is calculated
    reference parts: InsurancePart[];       // optionally, known parts can be included by reference
    reference basedOn: BaseProduct[];       // the BaseProducts from which the parts are taken

    riskAdjustment?: PercentageLiteral;     // an adjustment to the risk of the separate parts, e.g. caused by the combination of the parts
    calculation: DocuExpression;            // the premium as calculated based on the parts
    helpers: CalcFunction[];                // helper functions used to calculate the premium
&#125;</code></pre>
<p>The concept <code>InsurancePart</code> has a property <code>name: identifier</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L44-L45

concept InsurancePart &#123;
    name: identifier;                       // internal name</code></pre>
<SectionComponent tag="h2" 
  id="optional-properties-5"
  bind:intersecting={visibleById["optional-properties-5"]}
  onIntersectChange={(visible) => setVisible("optional-properties-5", visible)}
>Optional Properties</SectionComponent>
<p>Properties may be optional. This is indicated using a question mark after the property name.
Lists are always considered to be optional, i.e. they may be empty — there is no need for the question mark there.</p>
<p>As of Freon 2.0, primitive properties may also be defined as optional, just like other property types.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L70-L70

description?: Description;          // an optional description</code></pre>


<PrevNextSection {prevLink} {nextLink} />